import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { fmt, ARC_ROLE, ARC_STATUS, glyph, weightColors } from "./sceneModel";
import { pressable } from "../../../app/a11y";

function Close({ t, onClick }) {
  return (
    <span className="sc-close" title={t("Close")} aria-label={t("Close")} {...pressable(onClick)}>
      <CloseIcon />
    </span>
  );
}

// story arc details (in place of the scene list): summary, cast and its steps through the episode;
// a step expands to its scene card (expanded: scene index -> card)
export class ArcCard extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { chipsAll: false, overflow: false };
    this.chipsRef = React.createRef();
  }

  componentDidMount() {
    this.measure();
  }

  componentDidUpdate() {
    this.measure();
  }

  measure() {
    const el = this.chipsRef.current;
    if (!el || this.state.chipsAll) return;
    const overflow = el.scrollHeight > el.clientHeight + 2;
    if (overflow !== this.state.overflow) this.setState({ overflow });
  }

  render() {
    const { t, arc, expanded, onStep, onWho, onClose } = this.props;
    const w = weightColors(arc.weight);
    const { chipsAll, overflow } = this.state;
    const stats = [arc.steps.length === 1 ? t("1 scene") : t("{{count}} scenes", { count: arc.steps.length })];
    if (arc.status) stats.push(t(ARC_STATUS[arc.status] || arc.status));

    return (
      <div className="sc-detail sc-detail-main">
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span className="sc-weight" style={{ width: "20px", height: "20px", background: w[0], color: w[1], fontSize: "12px", lineHeight: "20px" }}>
            {arc.label}
          </span>
          <span className="sc-detail-title">{arc.title}</span>
          <Close t={t} onClick={onClose} />
        </div>
        <div className="sc-stats">{stats.join(" · ")}</div>
        {arc.summary && <div className="sc-prose">{arc.summary}</div>}
        {arc.characters.length > 0 && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
            <div ref={this.chipsRef} style={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", gap: "4px", maxHeight: chipsAll ? "none" : "23px", overflow: "hidden" }}>
              {arc.characters.map((n) => (
                <span key={n} className="sc-chip" title={t("Show this character")} {...pressable(() => onWho(n))}>
                  {n}
                </span>
              ))}
            </div>
            {(chipsAll || overflow) && (
              <span
                className="sc-link"
                style={{ padding: "2px 6px 2px 2px", lineHeight: "19px" }}
                {...pressable(() => this.setState({ chipsAll: !chipsAll }), { "aria-expanded": !!chipsAll })}
              >
                {chipsAll ? <ExpandLessIcon style={{ fontSize: "16px" }} /> : <ExpandMoreIcon style={{ fontSize: "16px" }} />}
                {chipsAll ? t("Show less") : t("Show all {{count}}", { count: arc.characters.length })}
              </span>
            )}
          </div>
        )}
        <div style={{ borderTop: "1px solid var(--p-line2)" }}>
          {arc.steps.map((q) => {
            const g = glyph(q.role),
              scene = expanded[q.i];
            return (
              // data-i while expanded, so the scene list scrolls the step (not just its card) into view
              <div key={q.i} data-i={scene ? q.i : undefined} style={{ borderBottom: "1px solid var(--p-line3)" }}>
                <div className="sc-steprow" {...pressable(() => onStep(q.i))} style={{ gridTemplateColumns: "118px minmax(0,1fr) 18px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "7px", font: "600 12px/18px var(--font-ui)", color: "var(--p-ink)" }}>
                      {/* glyphs are 6–10px; a 10px slot keeps the labels lined up */}
                      <span style={{ flex: "none", width: "10px", display: "flex", justifyContent: "center" }}>
                        <span
                          style={{
                            display: "inline-block",
                            flex: "none",
                            width: g.size + "px",
                            height: g.size + "px",
                            background: g.bg,
                            border: g.border,
                            borderRadius: g.radius,
                            transform: "rotate(" + g.rotate + ")",
                            boxSizing: "border-box",
                          }}
                        />
                      </span>
                      {t(ARC_ROLE[q.role] || q.role)}
                    </span>
                    <span className="sc-time">
                      {t("Scene")} {q.i + 1} · {fmt(q.t)}
                    </span>
                  </div>
                  <div className="sc-small" style={{ textWrap: "pretty" }}>
                    {q.headline && (
                      <>
                        <span style={{ fontWeight: 700, color: "var(--p-ink)" }}>{q.headline}</span> —{" "}
                      </>
                    )}
                    {q.note}
                  </div>
                  {/* on the first line of the note */}
                  <span style={{ alignSelf: "start", height: "21px", display: "flex", alignItems: "center", color: "var(--p-accent)" }}>
                    {scene ? <ExpandLessIcon style={{ fontSize: "18px" }} /> : <ExpandMoreIcon style={{ fontSize: "18px" }} />}
                  </span>
                </div>
                {scene && <div style={{ padding: "2px 0 10px" }}>{scene}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

function TimedRows({ rows, onSelect, cols }) {
  return rows.map((r, k) => (
    <div key={k} className="sc-steprow" {...pressable(() => onSelect(r.i, r.t))} style={{ gridTemplateColumns: cols, gap: "10px", padding: "3px 6px" }}>
      {r.kind !== undefined && <span className="sc-kind">{r.kind}</span>}
      {r.kind !== undefined ? (
        <span className="sc-small">
          {r.text} <span className="sc-time">{fmt(r.t)}</span>
        </span>
      ) : (
        <>
          <span className="sc-time" style={{ lineHeight: "21px" }}>
            {fmt(r.t)}
          </span>
          <span className="sc-small">{r.text}</span>
        </>
      )}
    </div>
  ));
}

// character details: presence, facts, beats and story steps
export class CharacterCard extends React.PureComponent {
  state = { stepsOpen: false };

  render() {
    const { t, who, thumb, screenTime, onSelect, onClose } = this.props;
    const { stepsOpen } = this.state;
    const stats = [who.present === 1 ? t("1 scene") : t("{{count}} scenes", { count: who.present })];
    // in how many of those scenes the character is central; nothing to say when none
    if (who.central > 0) stats.push(t("central in {{count}}", { count: who.central }));
    if (screenTime !== null && screenTime !== undefined) stats.push(t("{{time}} on screen", { time: fmt(screenTime) }));

    return (
      <div className="sc-detail">
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span className="sc-avatar" style={thumb ? { backgroundImage: "url('" + thumb + "')", backgroundSize: "cover", backgroundPosition: "center" } : undefined} />
          <span className="sc-detail-title">{who.name}</span>
          <Close t={t} onClick={onClose} />
        </div>
        <div className="sc-stats">{stats.join(" · ")}</div>
        {(who.facts.length > 0 || who.beats.length > 0 || who.steps.length > 0) && (
          // flows into two columns when the left column is wide
          <div className="sc-detail-cols">
            {who.facts.length > 0 && (
              <div>
                <div className="sc-overline" style={{ marginBottom: "2px" }}>
                  {t("Facts")}
                </div>
                <TimedRows rows={who.facts.map((f) => ({ ...f, kind: t(f.kind) }))} onSelect={onSelect} cols="74px minmax(0,1fr)" />
              </div>
            )}
            {who.beats.length > 0 && (
              <div>
                <div className="sc-overline" style={{ marginBottom: "2px" }}>
                  {t("Character beats")} ({who.beats.length})
                </div>
                <TimedRows rows={who.beats} onSelect={onSelect} cols="52px minmax(0,1fr)" />
              </div>
            )}
            {who.steps.length > 0 && (
              <div>
                <div className="sc-toggle" {...pressable(() => this.setState({ stepsOpen: !stepsOpen }), { "aria-expanded": !!stepsOpen })}>
                  {stepsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  <span>{t("Story steps in order ({{count}})", { count: who.steps.length })}</span>
                </div>
                {stepsOpen && (
                  <div style={{ marginTop: "2px" }}>
                    <TimedRows rows={who.steps} onSelect={onSelect} cols="52px minmax(0,1fr)" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}
